/**
 * <hero-waves> — procedurally generated, continuously flowing background waves.
 *
 * Draws a stack of layered waves on a <canvas>. Each wave is a sum of a few
 * sine harmonics whose phases advance over time, so the crests genuinely
 * travel across the section (alternating direction per layer) while their
 * amplitude slowly breathes. The pointer nudges the layers for a light
 * parallax. Rendering pauses while the section is off-screen or the tab is
 * hidden, and `prefers-reduced-motion` (or data-static) draws a single frame.
 *
 * Attributes:
 *   data-color   Wave colour. Any CSS colour, including #rrggbbaa.
 *   data-speed   Time multiplier (1 = default pace).
 *   data-count   Number of wave layers (default 6).
 *   data-static  Present = draw one frame, no animation.
 */
class HeroWaves extends HTMLElement {
  static observedAttributes = ['data-color', 'data-speed', 'data-static'];

  connectedCallback() {
    if (this.canvas) return;

    this.canvas = document.createElement('canvas');
    this.ctx = this.canvas.getContext('2d');
    this.appendChild(this.canvas);

    this.speed = parseFloat(this.dataset.speed) || 1;
    this.count = Math.max(1, parseInt(this.dataset.count, 10) || 6);
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.coarsePointer = window.matchMedia('(pointer: coarse)');
    this.rgb = this.#parseColor(this.dataset.color || '#ffffff');
    this.pointer = { x: 0.5, y: 0.5, targetX: 0.5, targetY: 0.5 };
    this.time = 0;
    this.lastFrame = 0;
    this.visible = false;
    this.frame = null;

    this.waves = this.#buildWaves();
    this.#resize();

    this.resizeObserver = new ResizeObserver(() => {
      this.#resize();
      if (!this.#animated()) this.#draw();
    });
    this.resizeObserver.observe(this);

    this.intersectionObserver = new IntersectionObserver(([entry]) => {
      this.visible = entry.isIntersecting;
      this.visible ? this.#start() : this.#stop();
    });
    this.intersectionObserver.observe(this);

    this.onVisibility = () => (document.hidden ? this.#stop() : this.visible && this.#start());
    document.addEventListener('visibilitychange', this.onVisibility);

    this.host = this.closest('section') || this;
    this.onPointerMove = (event) => {
      const rect = this.getBoundingClientRect();
      this.pointer.targetX = (event.clientX - rect.left) / rect.width;
      this.pointer.targetY = (event.clientY - rect.top) / rect.height;
    };
    this.onPointerLeave = () => {
      this.pointer.targetX = 0.5;
      this.pointer.targetY = 0.5;
    };
    this.host.addEventListener('pointermove', this.onPointerMove, { passive: true });
    this.host.addEventListener('pointerleave', this.onPointerLeave);

    this.#draw();
    this.classList.add('is-ready');
  }

  /** The theme editor updates attributes in place; keep the drawing in step. */
  attributeChangedCallback(name) {
    if (!this.canvas) return;
    if (name === 'data-color') this.rgb = this.#parseColor(this.dataset.color || '#ffffff');
    if (name === 'data-speed') this.speed = parseFloat(this.dataset.speed) || 1;
    if (this.#animated()) {
      this.visible && this.#start();
    } else {
      this.#stop();
      this.#draw();
    }
  }

  disconnectedCallback() {
    this.#stop();
    this.resizeObserver?.disconnect();
    this.intersectionObserver?.disconnect();
    document.removeEventListener('visibilitychange', this.onVisibility);
    this.host?.removeEventListener('pointermove', this.onPointerMove);
    this.host?.removeEventListener('pointerleave', this.onPointerLeave);
  }

  #animated() {
    return !this.hasAttribute('data-static') && !this.reducedMotion.matches;
  }

  #start() {
    if (this.frame || !this.#animated()) return;
    this.lastFrame = performance.now();
    this.frame = requestAnimationFrame(this.#tick);
  }

  #stop() {
    if (this.frame) cancelAnimationFrame(this.frame);
    this.frame = null;
  }

  #tick = (now) => {
    this.frame = requestAnimationFrame(this.#tick);
    // While the page is scrolling the canvas can wait: a frame of the waves is
    // worth far less than a frame of the scroll, and this is a full-width redraw.
    if (document.documentElement.classList.contains('is-scrolling')) {
      this.lastFrame = now;
      return;
    }
    // Slow motion doesn't need 60fps on phones; halve the work there.
    const minInterval = this.coarsePointer.matches ? 1000 / 30 : 0;
    if (now - this.lastFrame < minInterval) return;
    const dt = Math.min(now - this.lastFrame, 50) / 1000;
    this.lastFrame = now;
    this.time += dt * this.speed;
    this.pointer.x += (this.pointer.targetX - this.pointer.x) * 0.04;
    this.pointer.y += (this.pointer.targetY - this.pointer.y) * 0.04;
    this.#draw();
  };

  #resize() {
    const rect = this.getBoundingClientRect();
    const dpr = Math.min(window.devicePixelRatio || 1, 1.5);
    this.width = Math.max(1, Math.round(rect.width));
    this.height = Math.max(1, Math.round(rect.height));
    this.canvas.width = Math.round(this.width * dpr);
    this.canvas.height = Math.round(this.height * dpr);
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
  }

  /**
   * Each layer gets a baseline (fraction of the height), an amplitude that
   * grows with depth, three harmonics with incommensurate wavelengths and
   * speeds (so the shape never visibly repeats), and a travel direction.
   * Wavelengths are in CSS pixels rather than fractions of the width, so a
   * phone shows a gentle slope instead of a busier, compressed wave.
   */
  #buildWaves() {
    const waves = [];
    for (let i = 0; i < this.count; i += 1) {
      const depth = i / Math.max(1, this.count - 1); // 0 = top, 1 = bottom
      const direction = i % 2 === 0 ? 1 : -1;
      waves.push({
        baseline: 0.12 + depth * 0.62,
        amplitude: 0.035 + depth * 0.03,
        direction,
        harmonics: [
          { wavelength: 1500 - i * 110, speed: 0.16 + i * 0.02, phase: i * 1.7 },
          { wavelength: 640 - i * 38, speed: 0.09 + i * 0.013, phase: i * 0.9 + 2.1 },
          { wavelength: 2600 - i * 150, speed: 0.05 + i * 0.008, phase: i * 2.6 + 0.4 },
        ],
        breath: { speed: 0.07 + i * 0.011, phase: i * 1.3 },
        drift: { speed: 0.045 + i * 0.006, phase: i * 0.8 },
        strokeAlpha: 0.22,
        fillAlpha: 0.13,
      });
    }
    return waves;
  }

  #draw() {
    const { ctx, width: w, height: h, time: t } = this;
    const [r, g, b, baseAlpha] = this.rgb;
    const step = 6;
    ctx.clearRect(0, 0, w, h);

    this.waves.forEach((wave, index) => {
      const depth = (index + 1) / this.count;
      const parallaxX = (this.pointer.x - 0.5) * depth * 36;
      const parallaxY = (this.pointer.y - 0.5) * depth * 18;
      const breathing = 0.7 + 0.3 * Math.sin(t * wave.breath.speed + wave.breath.phase);
      const amplitude = wave.amplitude * h * breathing;
      const baseline = (wave.baseline + 0.012 * Math.sin(t * wave.drift.speed + wave.drift.phase)) * h + parallaxY;

      ctx.beginPath();
      let top = h;
      for (let x = -step; x <= w + step; x += step) {
        const px = x + parallaxX;
        let y = baseline;
        wave.harmonics.forEach((harmonic, k) => {
          const weight = k === 0 ? 0.6 : k === 1 ? 0.25 : 0.15;
          const u = (px / harmonic.wavelength) * Math.PI * 2;
          y += amplitude * weight * Math.sin(u + wave.direction * t * harmonic.speed * Math.PI * 2 + harmonic.phase);
        });
        if (y < top) top = y;
        x === -step ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
      }

      ctx.lineWidth = 1;
      ctx.strokeStyle = `rgba(${r}, ${g}, ${b}, ${baseAlpha * wave.strokeAlpha})`;
      ctx.stroke();

      const gradient = ctx.createLinearGradient(0, top, 0, h);
      gradient.addColorStop(0, `rgba(${r}, ${g}, ${b}, ${baseAlpha * wave.fillAlpha})`);
      gradient.addColorStop(1, `rgba(${r}, ${g}, ${b}, 0)`);
      ctx.lineTo(w + step, h + step);
      ctx.lineTo(-step, h + step);
      ctx.closePath();
      ctx.fillStyle = gradient;
      ctx.fill();
    });
  }

  /** Normalises any CSS colour to [r, g, b, alpha] via the canvas parser. */
  #parseColor(value) {
    const probe = document.createElement('canvas').getContext('2d');
    probe.fillStyle = '#000';
    probe.fillStyle = value;
    const parsed = probe.fillStyle;
    if (parsed.startsWith('#')) {
      const hex = parsed.slice(1);
      const int = parseInt(hex.length === 3 ? hex.replace(/./g, '$&$&') : hex, 16);
      return [(int >> 16) & 255, (int >> 8) & 255, int & 255, 1];
    }
    const parts = parsed.match(/[\d.]+/g)?.map(Number) || [255, 255, 255, 1];
    return [parts[0], parts[1], parts[2], parts.length > 3 ? parts[3] : 1];
  }
}

if (!customElements.get('hero-waves')) customElements.define('hero-waves', HeroWaves);
